import React from 'react';
import { Lock, Star, Crown } from 'lucide-react';

/**
 * CourseSelector - Horizontal course selection buttons
 */
export default function CourseSelector({ 
  courses = [1, 2, 3, 4],
  activeCourse = 1,
  courseStatus = [],
  onCourseChange
}) {
  const getCourseLabel = (courseNum) => {
    const labels = {
      1: 'Foundations',
      2: 'Builder',
      3: 'Artisan',
      4: 'Master'
    };
    return labels[courseNum] || `Course ${courseNum}`;
  };

  return (
    <div className="relative bg-slate-900/70 backdrop-blur-sm border-b border-slate-800/80" data-testid="course-selector">
      {/* Stone texture overlay */}
      <div 
        className="absolute inset-0 opacity-5"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
        }}
      />
      
      <div className="relative max-w-2xl mx-auto px-4 py-3">
        <div className="flex items-center justify-center gap-2">
          {courses.map((courseNum) => {
            const status = courseStatus.find(s => s.course === courseNum);
            // TESTING: All courses unlocked for testing
            const isLocked = false; // status && !status.is_unlocked;
            const isComplete = status?.is_complete;
            const isActive = courseNum === activeCourse;

            return (
              <button
                key={courseNum}
                onClick={() => !isLocked && onCourseChange?.(courseNum)}
                disabled={isLocked}
                className={`
                  relative group px-4 py-2 rounded-lg font-medium text-sm
                  transition-all duration-300 ease-out
                  ${isLocked 
                    ? 'opacity-40 cursor-not-allowed' 
                    : 'cursor-pointer hover:scale-105 active:scale-95'
                  }
                  ${isActive 
                    ? 'bg-gradient-to-br from-amber-600 via-amber-600 to-amber-700 text-white shadow-lg shadow-amber-600/40 border border-amber-500/50' 
                    : 'bg-slate-800/80 text-slate-300 border border-slate-700/50 hover:border-amber-600/40 hover:bg-slate-800 hover:text-amber-200'
                  }
                `}
                data-testid={`course-btn-${courseNum}`}
              >
                {/* Background glow for active */}
                {isActive && (
                  <div className="absolute inset-0 rounded-lg bg-amber-500/20 blur-sm -z-10" />
                )}

                {/* Lock icon */}
                {isLocked && (
                  <Lock className="h-3.5 w-3.5 mr-1.5 inline-block" />
                )}

                {/* Course number/label */}
                <span className="relative">
                  Course {courseNum}
                </span>

                {/* Completion star */}
                {isComplete && (
                  <Star className="h-3.5 w-3.5 ml-1.5 inline-block text-yellow-300 fill-yellow-300" />
                )}

                {/* Crown for master course */}
                {courseNum === 4 && !isLocked && (
                  <Crown className="h-3 w-3 ml-1 inline-block text-amber-300/70" />
                )}

                {/* Hover highlight */}
                {!isActive && !isLocked && (
                  <div className="absolute inset-0 rounded-lg bg-gradient-to-t from-amber-600/0 to-amber-600/10 opacity-0 group-hover:opacity-100 transition-opacity" />
                )}
              </button>
            );
          })}
        </div>

        {/* Course description */}
        <p className="text-center text-[10px] text-slate-500 mt-2 font-medium tracking-wide uppercase">
          {getCourseLabel(activeCourse)} • Words {(activeCourse - 1) * 500 + 1}-{activeCourse * 500}
        </p>
      </div>
    </div>
  );
}
